import type { Entity, Persisted } from '@tunasong/schemas'

/** The orders Home's Spaces section offers, in menu order; the first is the default */
export const spaceSortOrders = ['activity', 'name'] as const
export type SpaceSortOrder = (typeof spaceSortOrders)[number]

export const defaultSpaceSortOrder: SpaceSortOrder = 'activity'

export const spaceSortLabels: Record<SpaceSortOrder, string> = {
  activity: 'Recent activity',
  name: 'Name (A–Z)',
}

export function isSpaceSortOrder(value: unknown): value is SpaceSortOrder {
  return spaceSortOrders.some(order => order === value)
}

/**
 * Home lists spaces by last activity so active bands stay on top instead of sinking under old test folders.
 * `activityOf` gives a space's activity time: its newest direct child when Home has counted them, else its own
 * `updatedAt`. Ties fall back to the name for a stable order.
 */
export const sortSpacesByActivity = (
  spaces: Persisted<Entity>[],
  activityOf: (space: Persisted<Entity>) => string = space => space.updatedAt
): Persisted<Entity>[] =>
  [...spaces].sort((a, b) => activityOf(b).localeCompare(activityOf(a)) || (a.name ?? '').localeCompare(b.name ?? ''))

/**
 * Alphabetical, the way a person scans a long list: case-insensitive, numbers by value ("Band 2" before
 * "Band 10"), and unnamed spaces last rather than first.
 */
export const sortSpacesByName = (spaces: Persisted<Entity>[]): Persisted<Entity>[] =>
  [...spaces].sort((a, b) => {
    const aName = a.name ?? ''
    const bName = b.name ?? ''
    if (!aName || !bName) {
      return Number(!aName) - Number(!bName)
    }
    return aName.localeCompare(bName, undefined, { sensitivity: 'base', numeric: true })
  })

export const sortSpaces = (
  spaces: Persisted<Entity>[],
  order: SpaceSortOrder,
  activityOf?: (space: Persisted<Entity>) => string
): Persisted<Entity>[] => (order === 'name' ? sortSpacesByName(spaces) : sortSpacesByActivity(spaces, activityOf))

/**
 * Rows never move once the viewer has seen them (Fitts: no misclicks; Nielsen: things stay where they were). Keep
 * the order that was first shown; a space that appears later (just created) goes first, one that went away drops.
 */
export const applyFrozenOrder = (sorted: Persisted<Entity>[], frozenIds: string[]): Persisted<Entity>[] => {
  const position = new Map(frozenIds.map((id, index) => [id, index]))
  const added = sorted.filter(space => !position.has(space.id))
  const kept = sorted
    .filter(space => position.has(space.id))
    .sort((a, b) => (position.get(a.id) ?? 0) - (position.get(b.id) ?? 0))
  return [...added, ...kept]
}
